import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import GenericSelect from '../../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import TextInput from '../../../components/ui/TextInput.jsx';
import PersonCell from '../../../components/ui/PersonCell';
import {
  useEligiblePeriods, useScaleLevels,
  searchDepartments, searchUserGroups, searchEmployeesAsUsers,
  computeTargetsSum, buildSessionCreatePayload, isSessionFormValid,
  NEW_SESSION_INITIAL_FORM,
} from '../config';

const SectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 12px;
  &:first-child { margin-top: 0; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const HintText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #EA580C;
  background: #FFF7ED;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const InfoText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  background: #F1F5F9;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const TargetsBand = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  background: ${(p) => (p.$over ? '#FEF2F2' : '#F0FDF4')};
  color: ${(p) => (p.$over ? '#E53935' : '#16A34A')};
`;

const TargetLevelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const TargetLevelLabel = styled.span`
  flex: 1;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TargetInputWrap = styled.div`
  width: 110px;
`;

// Faz B (spec 2026-08-07 §4, Task 5) — Yeni Oturum formu: dönem+ad, kapsam
// (PeriodWizard adım-3 deseni), komite (1 moderatör + N üye — AsyncGenericSelect
// employee→userId düşüşü, bkz. config.searchEmployeesAsUsers), hedefler
// (dönem skalası varsa seviye başına % + Σ≤100 uyarısı) + tolerans.
//
// UX standardı düzeltmesi (2026-08-10, kullanıcı geri bildirimi): oluşturma
// akışı MODAL değil GenericFormDrawer'dır — GoalTemplatesTab ile AYNI desen:
// drawer yalnız KABUK (başlık/kaydet/iptal/overlay, schema=[]), karmaşık form
// içeriği children olarak sayfaya özgü kalır. onSave geçersiz formda false
// döner (drawer açık kalır), başarıda create sonucu truthy → drawer kapanır.
export default function NewSessionDrawer({ open, onClose, onCreate }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(NEW_SESSION_INITIAL_FORM);

  const { periods, periodsFetching } = useEligiblePeriods();
  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === form.periodId) || null,
    [periods, form.periodId],
  );
  const { levels, levelsFetching } = useScaleLevels(selectedPeriod?.ratingScaleId);

  // Drawer kapanıp açıldığında taslak sıfırdan başlar (modal unmount davranışının
  // drawer karşılığı — GoalTemplatesTab'ın hydrate deseniyle aynı).
  useEffect(() => {
    if (open) setForm(NEW_SESSION_INITIAL_FORM);
  }, [open]);

  // Dönem değişince o döneme ait olmayan hedefler taşınmasın diye sıfırlanır.
  useEffect(() => {
    setForm((f) => ({ ...f, levelTargets: {} }));
  }, [selectedPeriod?.ratingScaleId]);

  const setField = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const scopeTypeOptions = useMemo(() => ([
    { id: 'company', name: t('performance.calibrationSession.scope.company') },
    { id: 'department', name: t('performance.calibrationSession.scope.department') },
    { id: 'group', name: t('performance.calibrationSession.scope.group') },
  ]), [t]);

  const targetsSum = useMemo(() => computeTargetsSum(form.levelTargets), [form.levelTargets]);
  const valid = useMemo(() => isSessionFormValid(form), [form]);

  const setTarget = useCallback((levelId, value) => {
    setForm((f) => ({ ...f, levelTargets: { ...f.levelTargets, [levelId]: value } }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!valid) return false;
    const newId = await onCreate(buildSessionCreatePayload(form));
    return !!newId;
  }, [valid, onCreate, form]);

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t('performance.calibrationSession.new')}
      schema={[]}
      onSave={handleSave}
    >
      <SectionTitle>{t('performance.calibrationSession.form.basicsSection')}</SectionTitle>
      <Field>
        <GenericSelect
          label={t('performance.calibrationSession.form.period')}
          options={periodOptions}
          value={form.periodId}
          onChange={(v) => setField('periodId', v)}
          idKey="id"
          labelKey="name"
          required
        />
        {!periodsFetching && periods.length === 0 && (
          <InfoText>{t('performance.calibrationSession.form.noPeriodsHint')}</InfoText>
        )}
      </Field>
      <Field>
        <TextInput
          label={t('performance.calibrationSession.form.name')}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          required
        />
      </Field>

      <SectionTitle>{t('performance.calibrationSession.form.scopeSection')}</SectionTitle>
      <Field>
        <GenericSelect
          label={t('performance.calibrationSession.form.scopeType')}
          options={scopeTypeOptions}
          value={form.scopeType}
          onChange={(v) => setForm((f) => ({ ...f, scopeType: v, scopeTargetIds: [] }))}
          idKey="id"
          labelKey="name"
        />
      </Field>
      {form.scopeType === 'department' && (
        <Field>
          <AsyncGenericSelect
            label={t('performance.calibrationSession.form.scopeDepartments')}
            value={form.scopeTargetIds}
            onChange={(v) => setField('scopeTargetIds', v || [])}
            onSearch={searchDepartments}
            isMulti
            required
          />
        </Field>
      )}
      {form.scopeType === 'group' && (
        <Field>
          <AsyncGenericSelect
            label={t('performance.calibrationSession.form.scopeGroups')}
            value={form.scopeTargetIds}
            onChange={(v) => setField('scopeTargetIds', v || [])}
            onSearch={searchUserGroups}
            isMulti
            required
          />
        </Field>
      )}
      {form.scopeType !== 'company' && form.scopeTargetIds.length === 0 && (
        <HintText>{t('performance.calibrationSession.form.scopeTargetsRequiredHint')}</HintText>
      )}

      <SectionTitle>{t('performance.calibrationSession.form.committeeSection')}</SectionTitle>
      <Field>
        <AsyncGenericSelect
          label={t('performance.calibrationSession.form.moderator')}
          value={form.moderatorUserId}
          onChange={(v) => setField('moderatorUserId', v)}
          onSearch={searchEmployeesAsUsers}
          renderOption={(opt) => <PersonCell name={opt.name} profilePicture={opt.profilePicture} />}
          required
        />
      </Field>
      <Field>
        <AsyncGenericSelect
          label={t('performance.calibrationSession.form.members')}
          value={form.memberUserIds}
          onChange={(v) => setField('memberUserIds', v || [])}
          onSearch={searchEmployeesAsUsers}
          renderOption={(opt) => <PersonCell name={opt.name} profilePicture={opt.profilePicture} />}
          isMulti
        />
      </Field>

      {selectedPeriod?.ratingScaleId && (
        <>
          <SectionTitle>{t('performance.calibrationSession.form.targetsSection')}</SectionTitle>
          {!levelsFetching && levels.length > 0 && (
            <>
              <TargetsBand data-role="targets-sum-band" $over={targetsSum.isOverLimit}>
                {t('performance.calibrationSession.form.targetsSum')}: {targetsSum.sum}/100 {targetsSum.isOverLimit ? '⚠' : '✓'}
              </TargetsBand>
              {levels.slice().sort((a, b) => a.sortOrder - b.sortOrder).map((level) => (
                <TargetLevelRow key={level.id}>
                  <TargetLevelLabel>{level.label}</TargetLevelLabel>
                  <TargetInputWrap>
                    <TextInput
                      type="number"
                      min="0"
                      max="100"
                      placeholder="%"
                      value={form.levelTargets[level.id] ?? ''}
                      onChange={(e) => setTarget(level.id, e.target.value)}
                    />
                  </TargetInputWrap>
                </TargetLevelRow>
              ))}
              {targetsSum.isOverLimit && (
                <HintText>{t('performance.calibrationSession.form.targetsOverLimitHint')}</HintText>
              )}
            </>
          )}
          <Field>
            <TextInput
              label={t('performance.calibrationSession.form.tolerancePct')}
              type="number"
              min="0"
              value={form.tolerancePct}
              onChange={(e) => setField('tolerancePct', e.target.value)}
            />
          </Field>
        </>
      )}
    </GenericFormDrawer>
  );
}
